import { Link } from 'react-router-dom';
import {
  Target,
  Heart,
  Users,
  Shield,
  FileText,
  Mail,
  Building2,
  Award,
  CheckCircle,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import BackToTop from '../components/ui/BackToTop';

const boardMembers = [
  {
    id: 'eric-petross',
    name: 'Eric H. Petross',
    initials: 'EP',
    role: 'Founder & Director',
    bio: 'Leading platform development and organizational strategy.',
  },
  {
    id: 'mia-smith',
    name: 'Mia Eun Kyung Kim Smith',
    initials: 'MS',
    role: 'Director',
    bio: 'Leading strategic initiatives and organizational governance.',
  },
  {
    id: 'mary-louise-joseph',
    name: 'Mary-Louise Joseph',
    initials: 'MJ',
    role: 'Director',
    bio: 'Advising on organizational growth and impact measurement.',
  },
];

const impactStats = [
  {
    id: 'recidivism',
    value: '85%',
    label: 'Reduction in recidivism',
    detail: 'when individuals maintain stable employment',
  },
  {
    id: 'tech-jobs',
    value: '3.5M',
    label: 'Unfilled U.S. tech jobs',
    detail: 'a workforce gap our graduates are positioned to fill',
  },
  {
    id: 'salary',
    value: '$55–75K',
    label: 'Average starting salary',
    detail: 'for graduates entering technology roles',
  },
];

const transparencyItems = [
  { id: 'metrics', text: 'All program metrics publicly reported' },
  { id: 'financials', text: 'Financial statements available upon request' },
  { id: 'impact', text: 'Impact reports published quarterly once operational' },
  { id: '990', text: 'Annual 990 filings available on GuideStar' },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />

      <main id="main-content">
        {/* Hero Section */}
        <section
          aria-labelledby="hero-heading"
          className="relative bg-secondary-950 py-24 sm:py-32 overflow-hidden"
        >
          {/* Watermark banner logo — barely visible brand imprint */}
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <img
              src="/brand/2ndStoryBanner.png"
              alt=""
              className="w-[600px] sm:w-[800px] lg:w-[1000px] max-w-none opacity-[0.04] select-none"
              draggable="false"
            />
          </div>

          {/* Animated gradient overlay */}
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-15 animate-gradient-shift"
            style={{
              backgroundImage:
                'radial-gradient(circle at 25% 50%, rgba(53,94,59,0.4) 0%, transparent 50%), radial-gradient(circle at 75% 20%, rgba(53,94,59,0.2) 0%, transparent 45%)',
              backgroundSize: '200% 200%',
            }}
          />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-900/30 backdrop-blur-sm border border-primary-700/20 px-4 py-1.5 text-sm font-medium text-primary-300 mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-success-500" />
              </span>
              RI Nonprofit · Incorporated 2026
            </div>
            <h1
              id="hero-heading"
              className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight"
            >
              Building Second Chances
              <span className="block text-primary-400">Through Code</span>
            </h1>
            <p className="text-lg sm:text-xl text-secondary-300 max-w-2xl mx-auto leading-relaxed max-w-none">
              Empowering justice-impacted individuals to build meaningful careers in technology
              through education, mentorship, and community.
            </p>
          </div>
        </section>

        {/* Impact Stats Banner */}
        <section aria-label="Key impact statistics" className="bg-white border-b border-secondary-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-secondary-100">
              {impactStats.map((stat) => (
                <div key={stat.id} className="py-8 px-6 text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-1">{stat.value}</div>
                  <div className="text-sm font-semibold text-secondary-900 mb-1">{stat.label}</div>
                  <div className="text-xs text-secondary-500 leading-snug max-w-none">{stat.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section aria-labelledby="mission-heading" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">
                  Our Mission
                </p>
                <h2
                  id="mission-heading"
                  className="text-3xl font-bold text-secondary-900 mb-6"
                >
                  We believe everyone deserves a second story
                </h2>
                <p className="text-lg text-secondary-700 leading-relaxed mb-5 max-w-none">
                  By combining technology education with personal narrative development, we create
                  pathways to economic mobility for individuals who have been impacted by the
                  justice system.
                </p>
                <p className="text-lg text-secondary-700 leading-relaxed max-w-none">
                  Our dual-track approach builds both technical skills — the code — and
                  professional identity — the story — so graduates are prepared for every
                  dimension of a career transition.
                </p>
              </div>

              <div className="bg-secondary-50 rounded-2xl p-8 border border-secondary-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Heart className="h-5 w-5 text-primary-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900">Why We Exist</h3>
                </div>
                <ul className="space-y-5" role="list">
                  {impactStats.map((stat) => (
                    <li key={stat.id} className="flex items-start gap-3">
                      <TrendingUp
                        className="h-5 w-5 text-success-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span className="text-secondary-700 text-sm leading-relaxed max-w-none">
                        <strong className="text-secondary-900">{stat.value} {stat.label}</strong>
                        {' '}— {stat.detail}.
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Org Info Section */}
        <section aria-labelledby="org-heading" className="py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest mb-3">
                Organization
              </p>
              <h2
                id="org-heading"
                className="text-3xl font-bold text-secondary-900"
              >
                Transparency &amp; Governance
              </h2>
              <p className="mt-4 text-lg text-secondary-600 max-w-2xl mx-auto max-w-none">
                Accountability is core to our mission. Here is everything you need to know
                about Second Story as an organization.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Legal Status */}
              <div className="card flex flex-col">
                <div className="card-header pb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <Shield className="h-5 w-5 text-primary-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900">Legal Status</h3>
                  <p className="card-description">Official organization information</p>
                </div>
                <div className="card-content flex-1 space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-4 w-4 text-secondary-400 flex-shrink-0 mt-1" aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-0.5">
                        Founded
                      </p>
                      <p className="text-sm font-medium text-secondary-900">January 2026</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Award className="h-4 w-4 text-success-600 flex-shrink-0 mt-1" aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-0.5">
                        501(c)(3) Status
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="badge bg-accent-100 text-accent-800">In Preparation</span>
                        <span className="text-sm text-secondary-600">Application pending</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="h-4 w-4 text-secondary-400 flex-shrink-0 mt-1" aria-hidden="true" />
                    <div>
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide mb-0.5">
                        Tax ID (EIN)
                      </p>
                      <p className="text-sm font-medium text-secondary-900">42-2812566</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Board of Directors */}
              <div className="card flex flex-col">
                <div className="card-header pb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <Users className="h-5 w-5 text-primary-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900">Board of Directors</h3>
                  <p className="card-description">Guiding organizational strategy and governance</p>
                </div>
                <div className="card-content flex-1 space-y-3">
                  {boardMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-secondary-50 border border-secondary-100"
                    >
                      <div
                        className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0"
                        aria-hidden="true"
                      >
                        <span className="text-primary-700 font-semibold text-xs">
                          {member.initials}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-secondary-900 truncate">
                          {member.name}
                        </p>
                        <p className="text-xs text-primary-600 font-medium">{member.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Transparency Commitment */}
              <div className="card flex flex-col">
                <div className="card-header pb-4">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center mb-4">
                    <Target className="h-5 w-5 text-primary-600" aria-hidden="true" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900">
                    Transparency Commitment
                  </h3>
                  <p className="card-description">
                    Radical transparency and accountability in all operations
                  </p>
                </div>
                <div className="card-content flex-1">
                  <ul className="space-y-3" role="list">
                    {transparencyItems.map((item) => (
                      <li key={item.id} className="flex items-start gap-3">
                        <CheckCircle
                          className="h-4 w-4 text-success-600 flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span className="text-sm text-secondary-700 leading-snug max-w-none">
                          {item.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact / CTA Section */}
        <section
          aria-labelledby="contact-heading"
          className="py-20 bg-gradient-hero"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-sm font-semibold text-primary-400 uppercase tracking-widest mb-3">
              Get Involved
            </p>
            <h2
              id="contact-heading"
              className="text-3xl font-bold text-white mb-4"
            >
              Partner With Us
            </h2>
            <p className="text-lg text-secondary-300 mb-10 leading-relaxed max-w-none">
              Whether you are an employer, donor, volunteer, or prospective participant,
              we would love to connect and explore how we can build this mission together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/donate"
                className="btn btn-lg bg-primary-600 text-white hover:bg-primary-500 inline-flex items-center shadow-medium no-underline hover:no-underline"
              >
                <Heart className="mr-2 h-5 w-5" aria-hidden="true" />
                Support Our Mission
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Link>
              <a
                href="mailto:eHunter@straydog-secondstory.org"
                className="btn btn-lg border-2 border-white/70 text-white hover:bg-white/10 inline-flex items-center no-underline hover:no-underline"
                aria-label="Send email to eHunter@straydog-secondstory.org"
              >
                <Mail className="mr-2 h-5 w-5" aria-hidden="true" />
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
      <BackToTop />
    </div>
  );
};

export default AboutPage;
